// Les écrans du prologue, avant que le temps ne se mette à passer.
import { useEffect, useState } from 'preact/hooks';
import { HOUSING, MILIEUX, PATHS, PROJECTS } from '../content/paths';
import { fetchGlobalLives, livesInBrowser } from '../engine/compteur';
import { fmtDate, fmtEur } from '../engine/format';
import { t } from '../engine/i18n';
import type { GameState, HousingId, PathId, ProjectId } from '../engine/state';
import { update } from '../engine/store';
import { dateAtDay, isValidISODate, startOfLife, todayISO } from '../engine/time';
import {
  chooseHousing,
  chooseOverdraft,
  choosePath,
  chooseProject,
  finishSummer,
  openGift,
} from '../game/prologue';
import {
  BIKE_PRICE,
  BIKE_UNLOCK_EARNED,
  PIGGY_UNLOCK_EARNED,
  SUMMER_HOURS,
  buyBike,
  payPerClick,
  putInPiggy,
  work,
} from '../game/work';
import { Card, Meter } from './components';
import { TreatsPanel } from './Treats';
import { WellbeingGauge } from './Wellbeing';

export function PrologueScreen({ state }: { state: GameState }) {
  switch (state.prologueStep) {
    case 'gift':
      return <Gift state={state} />;
    case 'summer':
      return <Summer state={state} />;
    case 'endSummer':
      return <EndSummer state={state} />;
    case 'path':
      return <PathChoice state={state} />;
    case 'housing':
      return <HousingChoice />;
    case 'account':
      return <Account />;
    case 'project':
      return <ProjectChoice />;
    default:
      return null;
  }
}

const EARLIEST_BIRTH_DATE = '1940-01-01';

function Gift({ state }: { state: GameState }) {
  const [name, setName] = useState('');
  // Les vies déjà vécues : celles de ce navigateur, et le total de l'ensemble des joueurs.
  const lives = livesInBrowser();
  const [globalLives, setGlobalLives] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    void fetchGlobalLives().then((total) => {
      if (alive && total !== null) setGlobalLives(total);
    });
    return () => {
      alive = false;
    };
  }, []);
  const [birthDate, setBirthDate] = useState(state.birthDate);
  const valid = isValidISODate(birthDate) && birthDate >= EARLIEST_BIRTH_DATE && birthDate <= todayISO(Date.now());
  const startDate = valid ? startOfLife(birthDate) : null;

  return (
    <div class="story">
      <p class="story-lead">{t('prologue.birthday')}</p>
      <label class="field">
        <span>{t('prologue.nameLabel')}</span>
        <input
          type="text"
          value={name}
          maxLength={24}
          onInput={(event) => setName((event.target as HTMLInputElement).value)}
        />
      </label>
      <label class="field">
        <span>{t('prologue.birthDateLabel')}</span>
        <input
          type="date"
          value={birthDate}
          min={EARLIEST_BIRTH_DATE}
          max={todayISO(Date.now())}
          onInput={(event) => setBirthDate((event.target as HTMLInputElement).value)}
        />
      </label>
      {startDate ? (
        <p class="muted">{t('prologue.today', { date: fmtDate(dateAtDay(startDate, 0)) })}</p>
      ) : (
        <p class="warning">{t('prologue.invalidDate')}</p>
      )}
      <p>{t('prologue.gift')}</p>
      <button class="button-primary" disabled={!valid} onClick={() => update((s) => openGift(s, name, birthDate))}>
        {t('prologue.openGift')}
      </button>
      {(lives > 0 || globalLives !== null) && (
        <p class="hint">
          {lives > 0 && `${t('save.livesLocal', { count: lives })} `}
          {globalLives !== null && t('save.livesGlobal', { count: globalLives })}
        </p>
      )}
      <p class="credit">
        {t('credit.site')}
        <a href="https://www.lewel-labs.fr" target="_blank" rel="noopener">
          <img src="lewel-labs-logo.svg" alt="Lewel Labs" />
        </a>
      </p>
    </div>
  );
}

function Summer({ state }: { state: GameState }) {
  return (
    <div class="story">
      <h2>{t('prologue.summer.title')}</h2>
      <p>{t('prologue.summer.text')}</p>
      <Meter value={state.summerClicks} max={SUMMER_HOURS} label={t('prologue.summer.title')} />
      <p class="muted">{t('prologue.summer.progress', { done: state.summerClicks, total: SUMMER_HOURS })}</p>
      <button class="button-primary button-work" onClick={() => update(work)}>
        {t('work.summerButton', { amount: fmtEur(payPerClick(state)) })}
      </button>
      <p class="big-number">{t('prologue.summer.pocket', { amount: fmtEur(state.cash) })}</p>

      {state.summerEarned >= PIGGY_UNLOCK_EARNED && (
        <Card>
          <p>{t('prologue.piggy.content', { amount: fmtEur(state.piggy) })}</p>
          <div class="button-row">
            <button disabled={state.cash < 10} onClick={() => update((s) => putInPiggy(s, 10))}>
              {t('prologue.piggy.put')}
            </button>
            <button disabled={state.cash <= 0} onClick={() => update((s) => putInPiggy(s, s.cash))}>
              {t('prologue.piggy.putAll')}
            </button>
          </div>
          <p class="muted">{t('prologue.piggy.hint')}</p>
        </Card>
      )}

      {state.summerEarned >= BIKE_UNLOCK_EARNED && (
        <Card>
          {state.hasBike ? (
            <p>{t('prologue.bike.owned')}</p>
          ) : (
            <button disabled={state.cash < BIKE_PRICE} onClick={() => update(buyBike)}>
              {t('prologue.bike.offer', { price: fmtEur(BIKE_PRICE) })}
            </button>
          )}
        </Card>
      )}

      <Card>
        <WellbeingGauge state={state} />
        <TreatsPanel state={state} />
      </Card>
    </div>
  );
}

function EndSummer({ state }: { state: GameState }) {
  return (
    <div class="story">
      <h2>{t('prologue.endSummer.title')}</h2>
      <p>{t('prologue.endSummer.text', { amount: fmtEur(state.summerEarned) })}</p>
      <button class="button-primary" onClick={() => update(finishSummer)}>
        {t('common.continue')}
      </button>
    </div>
  );
}

function PathChoice({ state }: { state: GameState }) {
  const paths: PathId[] = ['work', 'alternance', 'studies'];
  return (
    <div class="story">
      <h2>{t('prologue.path.title')}</h2>
      <p>{t('prologue.path.text')}</p>
      <p class="muted">{t('prologue.path.situation', { milieu: t(MILIEUX[state.milieu].text) })}</p>
      <div class="choice-grid">
        {paths.map((id) => (
          <button class="choice-card" key={id} onClick={() => update((s) => choosePath(s, id))}>
            <strong>{t(PATHS[id].title)}</strong>
            <span>{t(PATHS[id].desc)}</span>
          </button>
        ))}
      </div>
      <p class="muted">{t('prologue.path.note')}</p>
    </div>
  );
}

function HousingChoice() {
  const options: HousingId[] = ['parents', 'coloc', 'studio'];
  return (
    <div class="story">
      <h2>{t('prologue.housing.title')}</h2>
      <div class="choice-grid">
        {options.map((id) => (
          <button class="choice-card" key={id} onClick={() => update((s) => chooseHousing(s, id))}>
            <strong>{t(HOUSING[id].title)}</strong>
            <span>{t(HOUSING[id].desc)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Account() {
  const [answer, setAnswer] = useState<boolean | null>(null);
  return (
    <div class="story">
      <h2>{t('prologue.account.title')}</h2>
      <p>{t('prologue.account.text')}</p>
      {answer === null ? (
        <div class="button-row">
          <button onClick={() => setAnswer(true)}>{t('prologue.account.accept')}</button>
          <button onClick={() => setAnswer(false)}>{t('prologue.account.refuse')}</button>
        </div>
      ) : (
        <>
          <p class="lesson">
            {t(answer ? 'prologue.account.acceptOutcome' : 'prologue.account.refuseOutcome')}
          </p>
          <button class="button-primary" onClick={() => update((s) => chooseOverdraft(s, answer))}>
            {t('common.continue')}
          </button>
        </>
      )}
    </div>
  );
}

function ProjectChoice() {
  const projects = Object.keys(PROJECTS) as ProjectId[];
  return (
    <div class="story">
      <h2>{t('prologue.project.title')}</h2>
      <div class="choice-grid">
        {projects.map((id) => (
          <button class="choice-card" key={id} onClick={() => update((s) => chooseProject(s, id))}>
            <strong>{t(PROJECTS[id].title)}</strong>
            <span>{t(PROJECTS[id].desc)}</span>
          </button>
        ))}
      </div>
      <p class="muted">{t('prologue.project.note')}</p>
    </div>
  );
}
